import React, { useEffect, useMemo } from 'react';

// --- add these imports at the top with your other Firebase imports ---
import { db } from '@root/firebase';
import {
  collection,
  addDoc,
  doc as fsDoc,
  updateDoc,
  increment,
  serverTimestamp,
} from 'firebase/firestore';

import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import ReadMoreTextBase from '@components/readmore/ReadMoreTextBase';
import Modal from '@/components/Modal';
import ThemedTributePreview from '@/components/ThemedTributePreview';
import Comments from '@components/Comments';
import GetViews from '@components/GetViews';
import { recordViewOnce } from '@/components/recordViewOnce';

function sanitizeId(val) {
  if (!val) return '';
  const s = String(val);
  const i = s.indexOf('/');
  return i >= 0 ? s.slice(0, i) : s;
}

function getDocId(tribute) {
  if (tribute?.id) return String(tribute.id); // Firestore doc id
  if (tribute?.tributeId) return sanitizeId(tribute.tributeId); // legacy
  return '';
}

function getPrivacy(tribute) {
  if (typeof tribute?.isPublic === 'boolean')
    return tribute.isPublic ? 'public' : 'private';
  const p = (
    tribute?.privacy ||
    tribute?.visibility ||
    'private'
  ).toLowerCase();
  return p === 'public' ? 'public' : 'private';
}

function getPrivacyMeta(tribute) {
  const isPublic = getPrivacy(tribute) === 'public';
  return {
    label: isPublic ? 'Public' : 'Private',
    badgeClass: isPublic ? 'bg-primary' : 'bg-danger',
    dotClass: isPublic ? 'bg-primary' : 'bg-danger',
    tooltip: isPublic
      ? 'Public: visible to everyone.'
      : 'Private: only you (and invited users) can view.',
  };
}

export default function TributeCard({
  tribute,
  currentUid,
  ownerUid, // usually tribute.uid
  showActions = true,
}) {
  const docId = getDocId(tribute);
  const isOwner = currentUid && ownerUid && currentUid === ownerUid;

  // A unique modal id per card so multiple cards can render safely
  const modalId = useMemo(
    () => `viewTribute-${docId || Math.random().toString(36).slice(2)}`,
    [docId]
  );

  useEffect(() => {
    if (!docId) {
      console.warn('[TributeCard] Missing documentId for tribute:', tribute);
    }
  }, [docId, tribute]);

  // init tooltips (for privacy)
  useEffect(() => {
    const els = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    els.forEach((el) => {
      try {
        if (el._tooltip) el._tooltip.dispose();
        if (window.bootstrap?.Tooltip)
          el._tooltip = new window.bootstrap.Tooltip(el);
      } catch {}
    });
    return () => {
      els.forEach((el) => {
        try {
          if (el._tooltip) el._tooltip.dispose();
        } catch {}
      });
    };
  }, [tribute]);

  const { badgeClass, dotClass, label, tooltip } = getPrivacyMeta(tribute);

  return (
    <div className='card contact-bx item-content h-100'>
      <div className='card-body user-profile pb-0'>
        <div className='d-flex align-items-center'>
          <div className='image-bx position-relative'>
            <div
              className='tooltip-container'
              style={{ display: 'inline-block', marginRight: '10px' }}
            >
              <img
                src={tribute?.image || '/images/default.jpg'}
                alt=''
                className='rounded-circle'
                style={{ width: 80, height: 80, objectFit: 'cover' }}
                onError={(e) => (e.currentTarget.src = '/images/default.jpg')}
              />

              <span
                className={`position-absolute rounded-circle ${dotClass}`}
                style={{
                  width: 24,
                  height: 24,
                  right: -2,
                  bottom: -2,
                  border: '2px solid #fff',
                }}
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                title={tooltip}
              />
            </div>
            <span className='tooltip'>{tooltip}</span>
          </div>

          <div className='ms-3 text-start'>
            <h4 className='fs-18 font-w600 mb-1'>
              {tribute?.mainTitle || 'Untitled'}
            </h4>
            <span className='mb-2 d-block'>
              {tribute?.subTitle || 'No Sub Title'}
            </span>
            {tribute?.tribute && (
              <span className='badge badge-primary light'>
                {tribute.tribute}
              </span>
            )}
          </div>
        </div>

        {tribute?.message && (
          <>
            <div className='user-meta-info mt-2'>
              <ReadMoreTextBase
                text={tribute.message || 'No Message to Show'}
                wordLimit={6}
                readMore={true}
              />
            </div>

            <div className={`row row-cols-${isOwner ? 6 : 5} g-2 mt-2`}>
              {/* Views */}
              <div className='col d-flex justify-content-center'>
                <GetViews docPath={`memorials/${docId}`} buttonSize='xs' />
              </div>

              {/* Like */}
              <div className='col d-flex justify-content-center'>
                {docId ? (
                  <LikeButton
                    collectionPath='memorials'
                    documentId={docId}
                    userId={currentUid}
                    styled={true}
                  />
                ) : (
                  <span className='opacity-0'>.</span>
                )}
              </div>

              {/* Favorite */}
              <div className='col d-flex justify-content-center'>
                {docId ? (
                  <FavoriteButton
                    collectionPath='memorials'
                    documentId={docId}
                    userId={currentUid}
                    styled={true}
                  />
                ) : (
                  <span className='opacity-0'>.</span>
                )}
              </div>

              {/* Preview */}
              <div className='col d-flex justify-content-center'>
                <div className='tooltip-container'>
                  <button
                    type='button'
                    className='reaction-btn reaction-action'
                    data-bs-toggle='modal'
                    data-bs-target={`#${modalId}`}
                    aria-label='View Tribute'
                    title='View Tribute'
                    onClick={() => {
                      // fire-and-forget; dedupes per user per day
                      void recordViewOnce({
                        docPath: `memorials/${docId}`,
                        userId: currentUid, // must be truthy; otherwise no-op
                      });
                    }}
                  >
                    <i className='fas fa-search reaction-icon' />
                  </button>
                  <span className='tooltip'>View Tribute</span>
                </div>
              </div>

              {/* Owner only */}
              {isOwner && (
                <div className='col d-flex justify-content-center'>
                  <div className='tooltip-container'>
                    <div className='dropdown'>
                      <button
                        type='button'
                        className='reaction-btn reaction-action'
                        data-bs-toggle='dropdown'
                        aria-expanded='false'
                        aria-label='Actions'
                        title='Actions'
                      >
                        <svg width='13' height='13' viewBox='0 0 24 24'>
                          <g fill='none' fillRule='evenodd'>
                            <rect width='24' height='24'></rect>
                            <circle fill='#000' cx='5' cy='12' r='2'></circle>
                            <circle fill='#000' cx='12' cy='12' r='2'></circle>
                            <circle fill='#000' cx='19' cy='12' r='2'></circle>
                          </g>
                        </svg>
                      </button>
                      <div className='dropdown-menu'>
                        <button className='dropdown-item' type='button'>
                          Edit
                        </button>
                        <button className='dropdown-item' type='button'>
                          Delete
                        </button>
                      </div>
                    </div>
                    <span className='tooltip'>Actions</span>
                  </div>
                </div>
              )}
            </div>
            <hr />
          </>
        )}

        {showActions && (
          <div className='col-12'>
            {/* Comments */}
            <div className='col d-flex justify-content-center'>
              <Comments
                parentId={docId}
                collectionPath='memorials'
                showCommentLabel={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal – receives all tribute attributes */}
      <Modal id={modalId} title='Tribute Preview' showFooter={false} size='lg'>
        <ThemedTributePreview
          tribute={tribute}
          docId={docId}
          currentUid={currentUid}
          showReactions={true}
        />
      </Modal>
    </div>
  );
}
